import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router, ActivatedRoute } from '@angular/router';
import { MenuGroupService, UpdateMenuGroupRequest } from '../../../services/menu-group.service';
import { ModuleService } from '../../../services/module.service';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoggerService } from '../../../core/services/logger.service';

@Component({
    selector: 'app-edit-menu-group-page',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatInputModule,
        MatSnackBarModule,
        MatCardModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './edit-menu-group-page.component.html',
    styleUrl: './edit-menu-group-page.component.scss',
})
export class EditMenuGroupPageComponent implements OnInit {
    form: FormGroup;
    isLoading = false;
    isLoadingGroup = false;
    id: string | null = null;
    /** Si el grupo ya tiene una api_key cargada (el valor real nunca llega al frontend). */
    hasApiKey = false;
    /** Ídem para la session_api_key (clave de sesiones). */
    hasSessionApiKey = false;

    constructor(
        private fb: FormBuilder,
        private menuGroupService: MenuGroupService,
        private moduleService: ModuleService,
        private snackBar: MatSnackBar,
        private router: Router,
        private route: ActivatedRoute,
        private logger: LoggerService
    ) {
        this.form = this.fb.group({
            nombre: ['', [Validators.required, Validators.maxLength(100)]],
            orden: [0, [Validators.required, Validators.min(0)]],
            integration: [false],
            url_token: ['', [Validators.maxLength(255)]],
            api_key: ['', [Validators.maxLength(255)]],
            session_api_key: ['', [Validators.maxLength(255)]],
        });

        this.form.get('integration')?.valueChanges.subscribe((isIntegration: boolean) => {
            if (!isIntegration) {
                this.form.get('url_token')?.setValue('');
                this.form.get('api_key')?.setValue('');
                this.hasApiKey = false;
                this.form.get('session_api_key')?.setValue('');
                this.hasSessionApiKey = false;
            }
        });
    }

    ngOnInit(): void {
        this.id = this.route.snapshot.paramMap.get('id');
        if (this.id) this.loadGroup();
        else {
            this.snackBar.open('ID de grupo no proporcionado', 'Cerrar', { duration: 3000, panelClass: ['error-snackbar'] });
            this.router.navigate(['/modules/menu-groups']);
        }
    }

    loadGroup(): void {
        if (!this.id) return;
        this.isLoadingGroup = true;
        this.menuGroupService
            .getMenuGroupById(this.id)
            .pipe(
                catchError((err) => {
                    this.logger.error('Error al cargar grupo', err as Error, 'EditMenuGroupPageComponent');
                    this.snackBar.open('Error al cargar el grupo', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    this.router.navigate(['/modules/menu-groups']);
                    return of(null);
                }),
                finalize(() => {
                    this.isLoadingGroup = false;
                })
            )
            .subscribe((res) => {
                if (res?.success && res.data) {
                    this.hasApiKey = res.data.hasApiKey;
                    this.hasSessionApiKey = res.data.hasSessionApiKey;
                    this.form.patchValue({
                        nombre: res.data.nombre,
                        orden: res.data.orden,
                        integration: res.data.integration,
                        url_token: res.data.urlToken ?? '',
                        // api_key nunca se precarga (el backend no la devuelve) —
                        // queda en blanco; dejarla así significa "no modificar".
                    });
                } else {
                    this.router.navigate(['/modules/menu-groups']);
                }
            });
    }

    getError(field: string): string {
        const c = this.form.get(field);
        if (!c) return '';
        if (c.hasError('required')) return 'Este campo es requerido';
        if (c.hasError('maxlength')) return `Máximo ${c.errors?.['maxlength']?.requiredLength} caracteres`;
        if (c.hasError('min')) return 'El valor debe ser mayor o igual a 0';
        return 'Campo inválido';
    }

    markTouched(): void {
        Object.keys(this.form.controls).forEach((k) => this.form.get(k)?.markAsTouched());
    }

    onSubmit(): void {
        if (!this.form.valid || !this.id) {
            this.markTouched();
            return;
        }
        this.isLoading = true;
        const v = this.form.value;
        const body: UpdateMenuGroupRequest = {
            nombre: v.nombre.trim(),
            orden: v.orden ?? 0,
            integration: !!v.integration,
            url_token: v.integration ? (v.url_token?.trim() || null) : null,
            // Si quedó en blanco, se omite -> el backend no toca la api_key
            // existente. Si integration pasó a false, el backend la borra
            // igual (ver menu-group.service.ts), aunque acá se mande algo.
            ...(v.integration && v.api_key?.trim() ? { api_key: v.api_key.trim() } : {}),
            ...(v.integration && v.session_api_key?.trim()
                ? { session_api_key: v.session_api_key.trim() }
                : {}),
        };
        this.menuGroupService
            .updateMenuGroup(this.id, body)
            .pipe(
                catchError((err) => {
                    this.logger.error('Error al actualizar grupo', err as Error, 'EditMenuGroupPageComponent');
                    this.snackBar.open(err.error?.message || 'Error al actualizar el grupo', 'Cerrar', {
                        duration: 5000,
                        panelClass: ['error-snackbar'],
                    });
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe((res) => {
                if (res?.success) {
                    this.moduleService.notifyModulesChanged();
                    this.snackBar.open('Grupo de menú actualizado exitosamente', 'Cerrar', {
                        duration: 3000,
                        panelClass: ['success-snackbar'],
                    });
                    this.router.navigate(['/modules/menu-groups']);
                }
            });
    }

    onCancel(): void {
        this.router.navigate(['/modules/menu-groups']);
    }
}
